import { cachePolicy } from '@pn/core/cachePolicy';
import {
  formatDataType,
  type DataItem,
  type Mapping,
} from '@pn/core/domain/data';
import { getScrollableQuery } from '@pn/core/domain/query';
import type { WorkspaceItem } from '@pn/core/domain/workspace';
import { handleError } from '@pn/core/errors/handleError';
import { dynamicDataProvider } from '@pn/core/services/data/dynamicDataProvider';
import { isCacheStale } from '@pn/core/utils/cache';
import { log } from '@pn/core/utils/debug';
import { hashSortedObject } from '@pn/services/utils/hash';

export type TableData = {
  data: DataItem[];
  totalCount: number;
  timestamp: number;
};

const tableDataCache = new Map<number, TableData>();

export async function getTableData(params: {
  item: WorkspaceItem;
  mapping: Mapping;
  pageSize: number;
  rethrow?: boolean;
}): Promise<TableData> {
  const { item, mapping, pageSize, rethrow } = params;

  if (item.dataSource.type === 'none') {
    return {
      data: [],
      totalCount: 0,
      timestamp: 0,
    };
  }

  const tableQuery = getScrollableQuery(item, pageSize);

  const hash = hashSortedObject(tableQuery);
  if (cachePolicy.tableData && tableDataCache.has(hash)) {
    const cacheCandidate = tableDataCache.get(hash)!;
    if (isCacheStale(cacheCandidate.timestamp)) {
      log(item.id).info('%ctableDataCache invalidated', 'color: #009688', hash);
      tableDataCache.delete(hash);
    } else {
      log(item.id).info('%ctableDataCache hit', 'color: #009688', hash);
      return cacheCandidate;
    }
  }

  const tableData: TableData = {
    data: [],
    totalCount: 0,
    timestamp: 0,
  };

  try {
    log(item.id).info(
      '%cfetching tableData from data source',
      'color: #009688'
    );
    const { data, totalCount } = await dynamicDataProvider(
      item.dataSource
    ).getDataByQuery({ query: tableQuery, mapping });

    Object.assign(tableData, { data, totalCount });
  } catch (error) {
    if (rethrow) throw error;
    handleError({
      error,
      notify: `Failed to get ${formatDataType(item.dataType)} table data`,
    });
  }

  if (cachePolicy.tableData) {
    tableData.timestamp = Date.now();
    tableDataCache.set(hash, tableData);
  }

  return tableData;
}
